/**
 * 文件管理 API
 *
 * 提供文件上传、下载、删除等功能
 */

import apiClient from './index'

/**
 * 上传的文件信息
 */
export interface UploadedFile {
  /** 文件 ID */
  id: string
  /** 文件名 */
  name: string
  /** 原始文件名 */
  original_name: string
  /** 文件描述 */
  description: string
  /** 文件类型 */
  file_type: string
  /** MIME 类型 */
  content_type: string
  /** 文件扩展名 */
  extension: string
  /** 文件大小（字节） */
  size: number
  /** 存储类型 */
  storage_type: string
  /** 存储路径 */
  storage_path: string
  /** 文件分类 */
  category: string
  /** 项目 ID */
  project_id: string
  /** 标签 */
  tags: string[]
  /** 状态 */
  status: string
  /** 上传者 */
  uploaded_by: string
  /** 创建时间 */
  created_at: string
  /** 更新时间 */
  updated_at: string
}

/**
 * 文件列表响应
 */
export interface FileListResponse {
  items: UploadedFile[]
  total: number
  page: number
  page_size: number
}

/**
 * 文件列表查询参数
 */
export interface FileListParams {
  /** 当前页码 */
  page?: number
  /** 每页数量 */
  page_size?: number
  /** 搜索关键字 */
  keyword?: string
  /** 文件分类 */
  category?: string
  /** 文件类型 */
  file_type?: string
  /** 项目 ID */
  project_id?: string
}

/**
 * 文件上传参数
 */
export interface FileUploadParams {
  /** 文件描述 */
  description?: string
  /** 文件分类 */
  category?: string
  /** 项目 ID */
  project_id?: string
  /** 标签 */
  tags?: string[]
}

/**
 * 获取文件列表
 */
export async function listFiles(params: FileListParams = {}): Promise<FileListResponse> {
  return apiClient.get<FileListResponse>('/files', { params })
}

/**
 * 获取文件详情
 */
export async function getFile(fileId: string): Promise<UploadedFile> {
  return apiClient.get<UploadedFile>(`/files/${fileId}`)
}

/**
 * 上传文件
 *
 * 注意：此方法仅用于单文件上传，多文件上传建议使用组件直接上传
 */
export async function uploadFile(
  file: File,
  params: FileUploadParams = {}
): Promise<UploadedFile> {
  const formData = new FormData()
  formData.append('file', file)

  if (params.description) formData.append('description', params.description)
  if (params.category) formData.append('category', params.category)
  if (params.project_id) formData.append('project_id', params.project_id)
  if (params.tags) formData.append('tags', JSON.stringify(params.tags))

  return apiClient.post<UploadedFile>('/files/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
}

/**
 * 批量上传文件
 */
export async function uploadFiles(
  files: File[],
  params: FileUploadParams = {}
): Promise<UploadedFile[]> {
  const formData = new FormData()
  files.forEach(file => {
    formData.append('files', file)
  })

  if (params.description) formData.append('description', params.description)
  if (params.category) formData.append('category', params.category)
  if (params.project_id) formData.append('project_id', params.project_id)
  if (params.tags) formData.append('tags', JSON.stringify(params.tags))

  return apiClient.post<UploadedFile[]>('/files/upload/batch', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
}

/**
 * 更新文件信息
 */
export async function updateFile(
  fileId: string,
  data: Partial<Pick<UploadedFile, 'name' | 'description' | 'category' | 'tags'>>
): Promise<UploadedFile> {
  return apiClient.put<UploadedFile>(`/files/${fileId}`, data)
}

/**
 * 删除文件
 */
export async function deleteFile(fileId: string): Promise<void> {
  await apiClient.delete(`/files/${fileId}`)
}

/**
 * 下载文件
 *
 * 使用 Blob 方式下载文件并触发浏览器下载
 */
export async function downloadFile(fileId: string, _fileName: string): Promise<void> {
  // 使用 window.open 方式下载，让浏览器处理
  window.open(`/api/v1/files/${fileId}/download`, '_blank')
}

/**
 * 获取文件下载 URL（用于直接访问）
 */
export function getFileDownloadUrl(fileId: string): string {
  return `/api/v1/files/${fileId}/download`
}

/**
 * 获取文件预览 URL（如果支持预览）
 */
export function getFilePreviewUrl(fileId: string): string {
  return `/api/v1/files/${fileId}/preview`
}
